import { useEffect, useState } from "react";
import { useAuth } from "context/AuthContext";
import { callGetAppMenus } from "api/MuyeonApi";
import { isProAs } from "constants/permissions";

/**
 * 하단 탭 목록 — 서버(app-menus surface=TAB)가 활동유형별로 준다. 탭바(Footer)와 레이아웃(DefaultLayout)이 같이 쓴다.
 *  레이아웃은 "지금 화면이 이 유형의 탭인가"로 뒤로가기·탭바를 정한다(meta.tabWhenInBar, 2026-10-24 커뮤니티).
 *
 *  ⚠️ 빈 배열을 그대로 쓰면 하단 탭이 통째로 사라져 앱을 돌아다닐 수 없게 된다.
 *     그래서 최종 방어선으로 하드코딩 폴백을 남겨 둔다.
 *  ⚠️ 다만 통신 실패마다 하드코딩으로 되돌아가면 **관리자가 꺼둔 탭이 다시 보인다.**
 *     마지막으로 성공한 응답을 활동유형별로 캐시해 두고, 실패하면 그것을 쓴다.
 *     하드코딩 폴백은 "캐시도 없을 때"(첫 실행부터 통신 불가)만 쓰인다.
 *  ⚠️ 활동유형이 바뀌면 다시 받는다(X-Active-Type 헤더 기준으로 서버가 고른다).
 *
 * @returns {{path:string, class:string, icon:string, title:string}[]}
 */
export default function useTabMenus() {
    const { activeType } = useAuth();
    // 활성유형이 구인구직 참여 유형(강사·무용수·학원·공연팀)이면 pro 탭, 아니면 일반(레슨) 탭.
    const isPro = isProAs(activeType);
    const [serverTabs, setServerTabs] = useState(() => readCache(activeType));

    useEffect(() => {
        let alive = true;
        // 응답이 오기 전에도 지난번 구성을 먼저 그린다(깜빡임·잘못된 탭 노출 방지).
        setServerTabs(readCache(activeType));
        callGetAppMenus("TAB")
            .then((r) => {
                if (!alive) return;
                const items = Array.isArray(r?.data?.items) ? r.data.items : null;
                if (items && items.length) {
                    setServerTabs(items);
                    try { localStorage.setItem(cacheKey(activeType), JSON.stringify(items)); } catch (e) { /* 용량 초과 등은 무시 */ }
                } else {
                    // 서버가 "탭 없음"을 명시한 경우. 캐시를 믿지 말고 폴백으로 간다.
                    setServerTabs(null);
                }
            })
            .catch(() => { if (alive) setServerTabs(readCache(activeType)); });
        return () => { alive = false; };
    }, [activeType]);

    // menuKey 'tab.casting' → nav 선택 키 'casting'. DOM 에는 안 나가고 선택 상태·React key 로만 쓴다.
    return serverTabs
        ? serverTabs.map((m) => ({
            path: m.path || "/home",
            class: String(m.key || "").split(".").pop() || m.key,
            icon: m.icon || "filled-home",
            title: m.title,
        }))
        : fallbackTabs(isPro);
}

const cacheKey = (activeType) => `tabMenus:${activeType || "GENERAL"}`;

function readCache(activeType) {
    try {
        const raw = localStorage.getItem(cacheKey(activeType));
        const arr = raw ? JSON.parse(raw) : null;
        return Array.isArray(arr) && arr.length ? arr : null;
    } catch (e) { return null; }
}

/* 폴백 — 서버 탭을 못 받았을 때 쓰는 종전 하드코딩.
   일반회원(취미)은 pro 전용 탭(캐스팅·대타공고) 대신 커뮤니티·공연을 노출.
   공연 = 관람 공연 정보(전용 엔티티, 캐스팅과 별개), 커뮤니티 = 취미무용 커뮤니티. */
function fallbackTabs(isPro) {
    return [
        { path: "/home", class: "home", icon: "filled-home", title: "홈" },
        isPro
            ? { path: "/jobs", class: "jobs", icon: "filled-note", title: "구인구직" }
            : { path: "/lessons", class: "lessons", icon: "filled-note", title: "강사" },
        isPro
            ? { path: "/casting", class: "casting", icon: "filled-star", title: "캐스팅" }
            : { path: "/community", class: "community", icon: "filled-community", title: "커뮤니티" },
        isPro
            ? { path: "/subs", class: "subs", icon: "filled-paper", title: "대타공고" }
            : { path: "/performance", class: "performance", icon: "filled-star", title: "공연" },
        { path: "/mypage", class: "my", icon: "filled-my", title: "MY" },
    ];
}
